import React, { forwardRef, useEffect, useState } from "react";
import styles from "./SavedGroupsPopup.module.scss";
import Spinner from "@/shared/ui/Spinner/Spinner";
import {
  useFetchSavedGroupsMutation,
  useSavedGroupsMutation,
} from "@/features/auth/api/authAPI";
import { ResponseSavedGroups } from "@/features/auth/types/auth";
import { toast } from "react-toastify";

interface SavedGroupsPopupProps {
  organizationId?: number;
  onSaveToGroup: (pointIds: number[], groupId: number) => void;
  savedPointIds: number[];
  setSavedPointIds: React.Dispatch<React.SetStateAction<number[]>>;
  onClose: () => void;
}

const SavedGroupsPopup = forwardRef<HTMLDivElement, SavedGroupsPopupProps>(
  (props, ref) => {
    const {
      organizationId,
      onSaveToGroup,
      savedPointIds,
      setSavedPointIds,
      onClose,
    } = props;
    const [fetchSavedGroups, { isLoading }] = useFetchSavedGroupsMutation();
    const [createGroup, { isLoading: isCreatingGroup }] =
      useSavedGroupsMutation();

    const [savedGroups, setSavedGroups] = useState<ResponseSavedGroups | null>(
      null,
    );
    const [showAddGroupPopup, setShowAddGroupPopup] = useState<boolean>(false);
    const [groupName, setGroupName] = useState<string>("");

    useEffect(() => {
      const loadSavedGroups = async () => {
        if (organizationId) {
          try {
            const response = await fetchSavedGroups(organizationId).unwrap();
            setSavedGroups(response);
          } catch (error) {
            console.error("Failed to fetch saved groups:", error);
          }
        }
      };

      loadSavedGroups();
    }, [organizationId, fetchSavedGroups]);

    const handleCreateNewGroup = async () => {
      try {
        const newGroup = await createGroup({
          organizationUuid: organizationId,
          name: groupName,
        }).unwrap();
        setSavedGroups((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            results: [newGroup, ...prev.results],
          };
        });
        setShowAddGroupPopup(false);
        setGroupName("");

        toast.success(`Created new group "${newGroup.name}"`);
        onSaveToGroup(savedPointIds, newGroup.id);
        setSavedPointIds([]);
        onClose();
      } catch (error) {
        console.error("Failed to create new group:", error);
        toast.error("Failed to create new group");
      }
    };

    const handleSaveToGroup = (groupId: number) => {
      onSaveToGroup(savedPointIds, groupId);
      setSavedPointIds([]);
      onClose();
    };

    return (
      <div className={styles.savePopup} ref={ref}>
        <div className={styles.savePopupContainer}>
          <button
            className={styles.savePopupAddBtn}
            onClick={() => setShowAddGroupPopup((prev) => !prev)}
          >
            <img src="/img/whiteIcon/PlusBold.svg" alt="Add Group" />
            <p>Add new collection</p>
          </button>
          <div className={styles.dividerContainer}>
            <div className={styles.divider} />
          </div>
          <div className={styles.savePopupGroupsContainer}>
            {isLoading || !savedGroups ? (
              <div className={styles.savePopupGroupsContainerSpinner}>
                <Spinner />
              </div>
            ) : (
              savedGroups.results.map((group, index) => (
                <button
                  key={index}
                  className={styles.savePopupGroup}
                  onClick={() => handleSaveToGroup(group.id)}
                >
                  <p>{group.name}</p>
                </button>
              ))
            )}
          </div>
        </div>

        {showAddGroupPopup && (
          <div className={styles.addGroupPopup}>
            <div className={styles.addGroupPopupContainer}>
              <div className={styles.addGroupPopupContainerHeader}>
                <img src="/img/whiteIcon/NewGroup.svg" alt="New Group" />
                <p>Add new collection</p>
              </div>
              <div className={styles.addGroupPopupInputContainer}>
                <input
                  type="text"
                  placeholder="Collection name"
                  onChange={(e) => setGroupName(e.target.value)}
                />
                <button onClick={handleCreateNewGroup}>
                  {isCreatingGroup ? "Adding..." : "Add"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

SavedGroupsPopup.displayName = "SavedGroupsPopup";

export default SavedGroupsPopup;
